import React, { useEffect, useState } from "react";
import { createPortal } from 'react-dom';
import { DndContext, closestCenter, DragOverlay } from '@dnd-kit/core';
import { SortableContext, rectSortingStrategy } from '@dnd-kit/sortable';
import { useSensors, useSensor, PointerSensor, TouchSensor, KeyboardSensor } from '@dnd-kit/core';
import { sortableKeyboardCoordinates } from '@dnd-kit/sortable';
import WorkbookCardDraggable from "./WorkbookCardDraggable";
import { getWorkbooks } from '@/hooks/content-generator/workbook-operations';
import { GRID_BREAKPOINTS } from '@/config/grid-breakpoints';
import { useWorkbook } from '@/contexts/workbook/WorkbookContext';
import { useWorkbookDnD } from '@/hooks/content-generator/use-workbook-dnd';
import { Skeleton } from '@/components/ui/skeleton';
import { FileX, FilePlus2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useImageCache } from '@/hooks/image/use-image-cache';
import { Project } from '@/hooks/content-generator/workbook-types';
import { motion } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Target, TrendingUp, CheckCircle } from 'lucide-react';

interface WorkbookListDnDProps {
  parentIsLoading?: boolean;
  parentWorkbookCount?: number;
  parentShowingCenteredLayout?: boolean;
}

const WorkbookListDnD: React.FC<WorkbookListDnDProps> = ({
  parentIsLoading,
  parentWorkbookCount,
  parentShowingCenteredLayout
}) => {
  const { filterStatus, selectedTagId, searchQuery, setSelectedProjectId, openCreateProjectModal } = useWorkbook();
  const [sortedItems, setSortedItems] = useState<Project[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();
  const { prefetchImages } = useImageCache();

  // Use parent loading state when available, otherwise use own state
  const effectiveIsLoading = parentIsLoading !== undefined ? parentIsLoading : isLoading;
  const effectiveWorkbookCount = parentWorkbookCount !== undefined ? parentWorkbookCount : sortedItems.length;

  // Direct data fetching (like File Storage pattern)
  const loadWorkbooks = async () => {
    try {
      // Only set loading if parent isn't managing it
      if (parentIsLoading === undefined) {
      setIsLoading(true);
      }
      setError(null);
      
      const result = await getWorkbooks({
        filterByStatus: filterStatus,
        filterByTag: selectedTagId || undefined,
        searchQuery: searchQuery
      });
      
      if (result.workbooks) {
        // Use database ordering directly (display_order ASC, updated_at DESC)
        // No client-side re-sorting needed since database query handles the order
        setSortedItems(result.workbooks);
      }
    } catch (err) {
      console.error('Failed to load workbooks:', err);
      setError('Failed to load workbooks. Please try again.');
    } finally {
      // Only set loading if parent isn't managing it
      if (parentIsLoading === undefined) {
      setIsLoading(false);
      }
    }
  };

  // Load data on mount and when filters change
  useEffect(() => {
    loadWorkbooks();
  }, [filterStatus, selectedTagId, searchQuery]);

  // DND functionality (pure local state management)
  const { 
    activeItem, 
    handleDragStart, 
    handleDragEnd,
    isUpdating 
  } = useWorkbookDnD({
    items: sortedItems,
    onItemsReorder: setSortedItems, // Direct local state update
  });

  // Prefetch cover images for better UX
  useEffect(() => {
    if (sortedItems?.length) {
      const coverImages = sortedItems
        .map(project => project.metadata?.coverImage as string | undefined)
        .filter(Boolean) as string[];
      
      if (coverImages.length) {
        prefetchImages(coverImages);
      }
    }
  }, [sortedItems, prefetchImages]);

  // Mobile-optimized sensors (matching File Storage pattern)
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 5,
      },
    }),
    useSensor(TouchSensor, {
      activationConstraint: {
        delay: 150,
        tolerance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  if (effectiveIsLoading) {
    return (
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-2 lg:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4 gap-2 md:gap-6">
        {Array.from({ length: 6 }).map((_, i) => (
          <div key={i} className="relative aspect-video rounded-lg overflow-hidden bg-gradient-to-br from-gray-100 to-gray-200 dark:from-gray-800 dark:to-gray-700 animate-pulse">
            <div className="absolute top-2 left-2 z-10">
              <Skeleton className="w-6 h-6 rounded-full" />
            </div>
            <div className="absolute top-2 right-2 z-10">
              <Skeleton className="w-6 h-6 rounded-full" />
            </div>
            <div className="absolute bottom-0 left-0 right-0 p-3">
              <Skeleton className="w-3/4 h-4 rounded" />
            </div>
          </div>
        ))}
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-10 text-muted-foreground">
        <p>{error}</p>
        <button 
          onClick={loadWorkbooks}
          className="mt-4 px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
        >
          Retry
        </button>
      </div>
    );
  }

  if (!effectiveIsLoading && (!sortedItems || sortedItems.length === 0)) {
    // If parent is showing centered layout, don't show empty state here
    if (parentShowingCenteredLayout) {
      return null;
    }
    
    return (
      <div className="text-center py-16 px-6">
        {filterStatus === 'all' && !selectedTagId && !searchQuery ? (
          // Detailed empty state for project-manager page (has more space)
          <div className="flex flex-col items-center justify-center px-8 py-12 min-h-[400px]">
            {/* Animated illustration */}
            <motion.div
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ delay: 0.2, duration: 0.5 }}
              className="relative mb-8"
            >
              {/* Animated background circles */}
              <div className="absolute inset-0 flex items-center justify-center">
                <motion.div
                  animate={{ rotate: 360 }}
                  transition={{ duration: 25, repeat: Infinity, ease: "linear" }}
                  className="w-32 h-32 rounded-full border-2 border-gradient-to-r from-purple-200/30 to-blue-200/30 dark:from-purple-800/30 dark:to-blue-800/30"
                />
                <motion.div
                  animate={{ rotate: -360 }}
                  transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
                  className="absolute w-24 h-24 rounded-full border border-dashed border-purple-300/40 dark:border-purple-700/40"
                />
              </div>
              
              {/* Target icon with floating animation */}
              <div className="relative z-10 w-24 h-24 bg-gradient-to-br from-purple-500 to-blue-600 rounded-3xl flex items-center justify-center shadow-2xl">
                <motion.div
                  animate={{ y: [0, -3, 0] }}
                  transition={{ duration: 2.5, repeat: Infinity, ease: "easeInOut" }}
                >
                  <Target className="w-12 h-12 text-white" />
                </motion.div>
              </div>
            </motion.div>

            {/* Content */}
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4, duration: 0.5 }}
              className="text-center mb-10 space-y-4 max-w-md"
            >
              <h3 className="text-2xl font-bold text-slate-800 dark:text-slate-200">
                Turn Dreams into Achievements
              </h3>
              <p className="text-slate-600 dark:text-slate-400 leading-relaxed">
                Watch AI break down your wildest dreams into bite-sized wins.
              </p>
            </motion.div>

            {/* Benefits */}
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.6, duration: 0.5 }}
              className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-10 w-full max-w-2xl"
            >
              {[
                { icon: Target, title: "Stay Focused", desc: "Clear direction for your efforts" },
                { icon: TrendingUp, title: "Track Progress", desc: "See how far you've come" },
                { icon: CheckCircle, title: "Celebrate Wins", desc: "Acknowledge your achievements" }
              ].map((benefit, index) => (
                <motion.div
                  key={benefit.title}
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: 0.7 + (index * 0.1), duration: 0.4 }}
                  className="text-center p-4 rounded-xl bg-gradient-to-br from-white/50 to-white/20 dark:from-slate-800/50 dark:to-slate-900/20 backdrop-blur-sm border border-white/20 dark:border-white/5"
                >
                  <div className="w-10 h-10 bg-gradient-to-br from-purple-100 to-blue-100 dark:from-purple-900/40 dark:to-blue-900/40 rounded-lg flex items-center justify-center mx-auto mb-3">
                    <benefit.icon className="w-5 h-5 text-purple-600 dark:text-purple-400" />
                  </div>
                  <h4 className="font-semibold text-sm text-slate-800 dark:text-slate-200 mb-1">{benefit.title}</h4>
                  <p className="text-xs text-slate-600 dark:text-slate-400">{benefit.desc}</p>
                </motion.div>
              ))}
            </motion.div>

            {/* CTA Button */}
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.8, duration: 0.5 }}
            >
              <Button 
                onClick={openCreateProjectModal}
                className="relative group overflow-hidden bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 text-white font-semibold py-4 px-8 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105"
              >
                {/* Background animation */}
                <div className="absolute inset-0 bg-gradient-to-r from-white/0 via-white/20 to-white/0 -translate-x-full group-hover:translate-x-full transition-transform duration-1000" />
                
                {/* Button content */}
                <div className="relative flex items-center gap-2">
                  <span>Create Your First Project</span>
                  <motion.div
                    animate={{ x: [0, 2, 0] }}
                    transition={{ duration: 1.5, repeat: Infinity, ease: "easeInOut" }}
                  >
                    →
                  </motion.div>
                </div>
              </Button>
            </motion.div>

            {/* Subtle helper text */}
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 1, duration: 0.5 }}
              className="text-xs text-slate-400 dark:text-slate-500 mt-6"
            >
              Start with one small goal and build momentum
            </motion.p>
          </div>
        ) : (
          // Filtered state (when showing search results or filters)
          <>
            <div className="relative mx-auto mb-6 w-20 h-20">
              <div className="absolute inset-0 bg-gradient-to-r from-purple-500/20 to-blue-500/20 rounded-full blur-xl"></div>
              <div className="relative w-full h-full bg-gradient-to-br from-purple-500 to-blue-600 rounded-2xl flex items-center justify-center shadow-lg">
                <FileX className="h-10 w-10 text-white" />
                <div className="absolute -top-2 -right-2">
                  <div className="h-4 w-4 text-yellow-400">✨</div>
                </div>
              </div>
            </div>

            <h3 className="text-2xl font-bold mb-3 bg-gradient-to-r from-gray-900 to-gray-700 dark:from-gray-100 dark:to-gray-300 bg-clip-text text-transparent">
              No projects match your filters
            </h3>
            
            <p className="text-gray-600 dark:text-gray-400 mb-8 max-w-md mx-auto leading-relaxed">
              Try changing your filters or search query to find more projects.
            </p>
          </>
        )}
      </div>
    );
  }

  const handleProjectClick = (workbook: Project) => {
    setSelectedProjectId(workbook.id);
                          navigate(`/project/${workbook.id}/tasks`);
  };

  // Get item IDs for SortableContext (matching File Storage pattern)
  const itemIds = sortedItems.map(item => item.id);

  return (
    <>
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
      >
        <SortableContext
          items={itemIds}
          strategy={rectSortingStrategy}
        >
          {/* The responsive grid - 1 column on mobile, matching existing layout on larger screens */}
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-2 lg:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4 gap-2 md:gap-6">
            {sortedItems.map((workbook) => (
              <WorkbookCardDraggable 
                key={workbook.id} 
                workbook={workbook} 
                onClick={handleProjectClick} 
              />
            ))}
          </div>
        </SortableContext>

        {/* Drag overlay portal (matching File Storage pattern) */}
        {typeof window !== 'undefined' && createPortal(
          <DragOverlay
            dropAnimation={null}
            modifiers={[]}
            zIndex={1000}
            style={{
              pointerEvents: 'none',
              willChange: 'transform',
              userSelect: 'none',
              touchAction: 'none',
            }}
          >
            {activeItem ? (
              <WorkbookCardDraggable 
                workbook={activeItem} 
                onClick={() => {}} // No-op for ghost
              />
            ) : null}
          </DragOverlay>,
          document.body
        )}
      </DndContext>
    </>
  );
};

export default WorkbookListDnD; 